import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { getTimestamp } from '../../shared/utils/helpers';
import { Coins, CreditCard, TrendingUp, Car, Trash2 } from 'lucide-react';

export default function FinancesPage({ settings }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const { data: rentals, loading: loadingRentals } = useCollection('rentals');
  const { data: vidanges, loading: loadingVidanges } = useCollection('vidanges');
  const { data: creditPayments, loading: loadingCredits } = useCollection('client_payments');
  const { data: expenses, loading: loadingExpenses, add: addExpense, remove: removeExpense } = useCollection('expenses');
  const { data: cars, loading: loadingCars } = useCollection('cars');
  const { data: staffPayments, loading: loadingStaff } = useCollection('staff_payments');
  const { data: cheques, loading: loadingCheques } = useCollection('cheques');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sortBy, setSortBy] = useState('revenue'); // revenue | profit | rentals | days | expenses | margin | name
  const [showAddForm, setShowAddForm] = useState(false);

  // Expense form states
  const [expTitle, setExpTitle] = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expDate, setExpDate] = useState(new Date().toISOString().split('T')[0]);
  const [expCategory, setExpCategory] = useState('Lavage');

  // Aggregate global stats
  const totalRentalsRevenue = useMemo(() => {
    return rentals.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0);
  }, [rentals]);

  const totalVidanges = useMemo(() => {
    if (settings.financeIncludeVidanges === false) return 0;
    return vidanges.reduce((sum, v) => sum + (parseFloat(v.cost) || 0), 0);
  }, [vidanges, settings.financeIncludeVidanges]);

  const totalCredits = useMemo(() => {
    if (settings.financeIncludeCredits === false) return 0;
    return creditPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [creditPayments, settings.financeIncludeCredits]);

  const totalGeneralExpenses = useMemo(() => {
    if (settings.financeIncludeExpenses === false) return 0;
    return expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  }, [expenses, settings.financeIncludeExpenses]);

  const totalStaffSalaries = useMemo(() => {
    if (settings.financeIncludeStaff === false) return 0;
    return staffPayments
      .filter(p => p.type === 'salary')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [staffPayments, settings.financeIncludeStaff]);

  const totalStaffAdvances = useMemo(() => {
    if (settings.financeIncludeStaffAdvances === false) return 0;
    return staffPayments
      .filter(p => p.type === 'advance' || p.type === 'bonus' || p.type === 'penalty')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [staffPayments, settings.financeIncludeStaffAdvances]);

  const totalChequesPaid = useMemo(() => {
    if (settings.financeIncludeChequesPaid === false) return 0;
    return cheques
      .filter(c => c.type === 'paid' && c.status === 'cashed')
      .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  }, [cheques, settings.financeIncludeChequesPaid]);

  const totalChequesReceived = useMemo(() => {
    if (settings.financeIncludeChequesReceived === false) return 0;
    return cheques
      .filter(c => c.type === 'received' && c.status === 'cashed')
      .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  }, [cheques, settings.financeIncludeChequesReceived]);

  const totalBankInstallments = useMemo(() => {
    if (settings.financeIncludeBankInstallments === false) return 0;
    let total = 0;
    cars.forEach(c => {
      if (c.bankInstallments) {
        c.bankInstallments.forEach(inst => {
          if (inst.paid) {
            total += parseFloat(inst.paidAmount || inst.amount) || 0;
          }
        });
      }
    });
    return total;
  }, [cars, settings.financeIncludeBankInstallments]);

  const totalExpenses = useMemo(() => {
    return totalVidanges + totalCredits + totalGeneralExpenses + totalStaffSalaries + totalStaffAdvances + totalChequesPaid + totalBankInstallments;
  }, [totalVidanges, totalCredits, totalGeneralExpenses, totalStaffSalaries, totalStaffAdvances, totalChequesPaid, totalBankInstallments]);

  const totalRevenue = useMemo(() => {
    return totalRentalsRevenue + totalChequesReceived;
  }, [totalRentalsRevenue, totalChequesReceived]);

  const netProfit = useMemo(() => {
    return totalRevenue - totalExpenses;
  }, [totalRevenue, totalExpenses]);

  // PER-CAR STATS
  const carStats = useMemo(() => {
    const stats = {};
    
    // Initialize
    cars.forEach(c => {
      stats[c.id] = {
        carId: c.id,
        carName: `${c.brand} ${c.model}`,
        plate: c.plate,
        revenue: 0,
        rentalsCount: 0,
        totalDays: 0,
        vidangesCost: 0,
        vidangesCount: 0,
        creditPayments: 0,
        bankInstallmentsPaid: 0,
        netProfit: 0,
        avgRevenuePerDay: 0,
        avgRevenuePerRental: 0,
        lastRentalDate: null,
        status: c.status
      };
    });
    
    // Rentals
    rentals.forEach(r => {
      if (r.status === 'cancelled') return;
      const carId = r.carId;
      if (!carId) return;
      
      if (!stats[carId]) {
        stats[carId] = {
          carId,
          carName: r.carName || 'Voiture inconnue',
          plate: '—',
          revenue: 0,
          rentalsCount: 0,
          totalDays: 0,
          vidangesCost: 0,
          vidangesCount: 0,
          creditPayments: 0,
          bankInstallmentsPaid: 0,
          netProfit: 0,
          avgRevenuePerDay: 0,
          avgRevenuePerRental: 0,
          lastRentalDate: null,
          status: 'deleted'
        };
      }
      
      stats[carId].revenue += parseFloat(r.totalAmount) || 0;
      stats[carId].rentalsCount += 1;
      stats[carId].totalDays += parseInt(r.days || r.totalDays) || 0;
      if (!stats[carId].lastRentalDate || r.startDate > stats[carId].lastRentalDate) {
        stats[carId].lastRentalDate = r.startDate;
      }
    });
    
    // Vidanges
    vidanges.forEach(v => {
      if (stats[v.carId]) {
        stats[v.carId].vidangesCost += parseFloat(v.cost) || 0;
        stats[v.carId].vidangesCount += 1;
      }
    });
    
    // Credits
    creditPayments.forEach(p => {
      if (stats[p.carId]) {
        stats[p.carId].creditPayments += parseFloat(p.amount) || 0;
      }
    });
    
    // Bank traites
    cars.forEach(c => {
      if (c.bankInstallments && stats[c.id]) {
        c.bankInstallments.forEach(inst => {
          if (inst.paid) {
            stats[c.id].bankInstallmentsPaid += parseFloat(inst.paidAmount || inst.amount) || 0;
          }
        });
      }
    });
    
    // Averages and net
    Object.values(stats).forEach(s => {
      const totalCarExpenses = s.vidangesCost + s.creditPayments + s.bankInstallmentsPaid;
      s.totalExpenses = totalCarExpenses;
      s.netProfit = s.revenue - totalCarExpenses;
      s.avgRevenuePerDay = s.totalDays > 0 ? s.revenue / s.totalDays : 0;
      s.avgRevenuePerRental = s.rentalsCount > 0 ? s.revenue / s.rentalsCount : 0;
      s.profitMargin = s.revenue > 0 ? (s.netProfit / s.revenue) * 100 : 0;
    });
    
    return Object.values(stats);
  }, [cars, rentals, vidanges, creditPayments]);

  const sortedCarStats = useMemo(() => {
    const sorted = [...carStats];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'revenue': return b.revenue - a.revenue;
        case 'profit': return b.netProfit - a.netProfit;
        case 'rentals': return b.rentalsCount - a.rentalsCount;
        case 'days': return b.totalDays - a.totalDays;
        case 'expenses': return b.totalExpenses - a.totalExpenses;
        case 'margin': return b.profitMargin - a.profitMargin;
        case 'name': return a.carName.localeCompare(b.carName);
        default: return b.revenue - a.revenue;
      }
    });
    return sorted;
  }, [carStats, sortBy]);

  const topPerformer = useMemo(() => {
    return carStats.length > 0 
      ? [...carStats].sort((a, b) => b.netProfit - a.netProfit)[0] 
      : null;
  }, [carStats]);
  
  const worstPerformer = useMemo(() => {
    return carStats.length > 0 
      ? [...carStats].filter(c => c.rentalsCount > 0).sort((a, b) => a.netProfit - b.netProfit)[0] 
      : null;
  }, [carStats]);

  // Group by day
  const dailyStats = useMemo(() => {
    const days = {};
    
    rentals.forEach(r => {
      const date = r.startDate || (r.createdAt && new Date(r.createdAt).toISOString().split('T')[0]);
      if (!date) return;
      if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
      days[date].revenue += parseFloat(r.totalAmount) || 0;
    });

    if (settings.financeIncludeVidanges !== false) {
      vidanges.forEach(v => {
        const date = v.date;
        if (!date) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].expenses += parseFloat(v.cost) || 0;
      });
    }

    if (settings.financeIncludeCredits !== false) {
      creditPayments.forEach(p => {
        const date = p.date;
        if (!date) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].expenses += parseFloat(p.amount) || 0;
      });
    }

    if (settings.financeIncludeExpenses !== false) {
      expenses.forEach(e => {
        const date = e.date;
        if (!date) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].expenses += parseFloat(e.amount) || 0;
      });
    }

    if (settings.financeIncludeStaff !== false || settings.financeIncludeStaffAdvances !== false) {
      staffPayments.forEach(p => {
        const date = p.paymentDate || p.date;
        if (!date) return;
        const isSalary = p.type === 'salary';
        const isAdvance = p.type === 'advance' || p.type === 'bonus' || p.type === 'penalty';
        if (isSalary && settings.financeIncludeStaff === false) return;
        if (isAdvance && settings.financeIncludeStaffAdvances === false) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].expenses += parseFloat(p.amount) || 0;
      });
    }

    if (settings.financeIncludeChequesPaid !== false) {
      cheques.filter(c => c.type === 'paid' && c.status === 'cashed').forEach(c => {
        const date = c.dueDate || c.createdAt;
        if (!date) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].expenses += parseFloat(c.amount) || 0;
      });
    }
    if (settings.financeIncludeChequesReceived !== false) {
      cheques.filter(c => c.type === 'received' && c.status === 'cashed').forEach(c => {
        const date = c.dueDate || c.createdAt;
        if (!date) return;
        if (!days[date]) days[date] = { date, revenue: 0, expenses: 0 };
        days[date].revenue += parseFloat(c.amount) || 0;
      });
    }

    return Object.values(days).sort((a, b) => b.date.localeCompare(a.date));
  }, [rentals, vidanges, creditPayments, expenses, staffPayments, cheques, settings]);

  // Group by month
  const monthlyStats = useMemo(() => {
    const months = {};
    const getMonth = (dateStr) => dateStr ? dateStr.substring(0, 7) : '';

    rentals.forEach(r => {
      const month = getMonth(r.startDate);
      if (!month) return;
      if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
      months[month].revenue += parseFloat(r.totalAmount) || 0;
    });

    if (settings.financeIncludeVidanges !== false) {
      vidanges.forEach(v => {
        const month = getMonth(v.date);
        if (!month) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].expenses += parseFloat(v.cost) || 0;
      });
    }

    if (settings.financeIncludeCredits !== false) {
      creditPayments.forEach(p => {
        const month = getMonth(p.date);
        if (!month) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].expenses += parseFloat(p.amount) || 0;
      });
    }

    if (settings.financeIncludeExpenses !== false) {
      expenses.forEach(e => {
        const month = getMonth(e.date);
        if (!month) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].expenses += parseFloat(e.amount) || 0;
      });
    }

    if (settings.financeIncludeStaff !== false || settings.financeIncludeStaffAdvances !== false) {
      staffPayments.forEach(p => {
        const date = p.paymentDate || p.date;
        const month = getMonth(date);
        if (!month) return;
        const isSalary = p.type === 'salary';
        const isAdvance = p.type === 'advance' || p.type === 'bonus' || p.type === 'penalty';
        if (isSalary && settings.financeIncludeStaff === false) return;
        if (isAdvance && settings.financeIncludeStaffAdvances === false) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].expenses += parseFloat(p.amount) || 0;
      });
    }

    if (settings.financeIncludeChequesPaid !== false) {
      cheques.filter(c => c.type === 'paid' && c.status === 'cashed').forEach(c => {
        const month = getMonth(c.dueDate || c.createdAt);
        if (!month) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].expenses += parseFloat(c.amount) || 0;
      });
    }
    if (settings.financeIncludeChequesReceived !== false) {
      cheques.filter(c => c.type === 'received' && c.status === 'cashed').forEach(c => {
        const month = getMonth(c.dueDate || c.createdAt);
        if (!month) return;
        if (!months[month]) months[month] = { month, revenue: 0, expenses: 0 };
        months[month].revenue += parseFloat(c.amount) || 0;
      });
    }

    return Object.values(months).sort((a, b) => b.month.localeCompare(a.month));
  }, [rentals, vidanges, creditPayments, expenses, staffPayments, cheques, settings]);

  const chartData = useMemo(() => {
    const months = [];
    const d = new Date();
    for (let i = 0; i < 6; i++) {
      const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const found = monthlyStats.find(x => x.month === monthStr) || { month: monthStr, revenue: 0, expenses: 0 };
      months.push(found);
      d.setMonth(d.getMonth() - 1);
    }
    return months.reverse(); // Chronological
  }, [monthlyStats]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expTitle || !expAmount) return alert(t('alert_fill_required'));
    try {
      await addExpense({
        title: expTitle,
        amount: parseFloat(expAmount) || 0,
        date: expDate,
        category: expCategory,
        createdAt: getTimestamp()
      });
      setExpTitle('');
      setExpAmount('');
      setExpDate(new Date().toISOString().split('T')[0]);
      setExpCategory('Lavage');
      setShowAddForm(false);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (confirm(t('confirm_delete_expense'))) {
      try {
        await removeExpense(id);
      } catch (err) {
        alert('❌ ' + err.message);
      }
    }
  };

  const formattedValue = (val) => `${parseFloat(val || 0).toLocaleString()} ${settings.currency || 'MAD'}`;

  // SVG Chart Setup
  const maxChartVal = Math.max(...chartData.map(d => Math.max(d.revenue, d.expenses)), 1000);
  const svgW = 600;
  const svgH = 260;
  const padX = 50;
  const padY = 30;
  const graphW = svgW - 2 * padX;
  const graphH = svgH - 2 * padY;
  const step = graphW / 6;

  const categories = [
    { key: 'Lavage', label: t('cat_lavage') },
    { key: 'Assurance', label: t('cat_assurance') },
    { key: 'Carburant', label: t('cat_carburant') },
    { key: 'Salaires', label: t('cat_salaires') },
    { key: 'Loyer', label: t('cat_loyer') },
    { key: 'Autre', label: t('cat_autre') }
  ];

  if (loadingRentals || loadingVidanges || loadingCredits || loadingExpenses || loadingCars || loadingStaff || loadingCheques) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('finances_title')}</h1>
        <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-200">
          {`${t('currency_label')} ${settings.currency || 'MAD'}`}
        </span>
      </div>

      {/* Verdict Panel */}
      {netProfit >= 0 ? (
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-green-200 rounded-2xl p-6 shadow-sm flex items-center justify-between gap-4 transition-all duration-300">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl animate-bounce">🏆</span>
              <h2 className="text-lg font-bold text-emerald-800">{t('profit_verdict_title')}</h2>
            </div>
            <p className="text-sm text-emerald-700 leading-relaxed max-w-xl font-medium">
              {t('profit_verdict_msg_pre')}{' '}
              <strong className="text-emerald-950 text-base underline decoration-double font-bold">
                {formattedValue(netProfit)}
              </strong>{' '}
              {t('profit_verdict_msg_post')}
            </p>
          </div>
          <div className={`hidden md:flex flex-col ${isAr ? 'items-start' : 'items-end'}`}>
            <span className="text-xs uppercase tracking-wider text-emerald-600 font-bold">{t('net_profit_global')}</span>
            <span className="text-3xl font-black text-emerald-700 mt-1">+{formattedValue(netProfit)}</span>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-rose-50 to-red-50 border border-red-200 rounded-2xl p-6 shadow-sm flex items-center justify-between gap-4 transition-all duration-300">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl animate-pulse">⚠️</span>
              <h2 className="text-lg font-bold text-rose-800">{t('loss_verdict_title')}</h2>
            </div>
            <p className="text-sm text-rose-700 leading-relaxed max-w-xl font-medium">
              {t('loss_verdict_msg_pre')}{' '}
              <strong className="text-rose-950 text-base underline font-bold">
                {formattedValue(Math.abs(netProfit))}
              </strong>{' '}
              {t('loss_verdict_msg_post')}
            </p>
          </div>
          <div className={`hidden md:flex flex-col ${isAr ? 'items-start' : 'items-end'}`}>
            <span className="text-xs uppercase tracking-wider text-rose-600 font-bold">{t('net_deficit_global')}</span>
            <span className="text-3xl font-black text-rose-700 mt-1">{formattedValue(netProfit)}</span>
          </div>
        </div>
      )}

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-center justify-between shadow-sm hover:shadow transition-all duration-205">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('ca_revenus')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1.5">{formattedValue(totalRevenue)}</p>
            <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1 font-semibold">
              {totalChequesReceived > 0 
                ? `🟢 ${t('total_locations_label')} + 💵 ${isAr ? 'شيكات' : 'Chèques'}`
                : `🟢 ${t('total_locations_label')}`}
            </p>
          </div>
          <div className="p-3.5 bg-green-50 text-green-600 rounded-xl">
            <Coins className="w-6 h-6" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-center justify-between shadow-sm hover:shadow transition-all duration-205">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('depenses_totales')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1.5">{formattedValue(totalExpenses)}</p>
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1 font-semibold">
              {(() => {
                const parts = [];
                if (totalVidanges > 0) parts.push(isAr ? 'صيانة' : 'Vid.');
                if (totalCredits > 0) parts.push(isAr ? 'أقساط' : 'Créd.');
                if (totalStaffSalaries > 0) parts.push(isAr ? 'رواتب' : 'Sal.');
                if (totalChequesPaid > 0) parts.push(isAr ? 'شيكات' : 'Chèq.');
                if (totalBankInstallments > 0) parts.push(isAr ? 'ترايتات' : 'Trait.');
                if (totalGeneralExpenses > 0) parts.push(isAr ? 'عامة' : 'Gén.');
                return `🔧 ${parts.join(' + ') || (isAr ? 'لا مصاريف' : 'Aucune')}`;
              })()}
            </p>
          </div>
          <div className="p-3.5 bg-red-50 text-red-550 text-red-500 rounded-xl">
            <CreditCard className="w-6 h-6" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-center justify-between shadow-sm hover:shadow transition-all duration-205">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('net_profit_title')}</p>
            <p className={`text-2xl font-bold mt-1.5 ${netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formattedValue(netProfit)}
            </p>
            <p className={`text-xs mt-1.5 flex items-center gap-1 font-semibold ${netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {netProfit >= 0 ? `📈 ${t('after_charges')}` : `📉 ${t('after_charges')}`}
            </p>
          </div>
          <div className={`p-3.5 rounded-xl ${netProfit >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
            <TrendingUp className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 gap-1 overflow-x-auto">
        {[
          { id: 'dashboard', label: t('tab_overview'), emoji: '📊' },
          { id: 'perCar', label: isAr ? 'حسب السيارة' : 'Par voiture', emoji: '🚗' },
          { id: 'monthly', label: t('tab_monthly_follow'), emoji: '📅' },
          { id: 'daily', label: t('tab_daily_follow'), emoji: '📆' },
          { id: 'generalExpenses', label: t('tab_general_expenses'), emoji: '💸' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
              activeTab === tab.id 
                ? 'border-blue-600 text-blue-600 font-bold' 
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}
          >
            <span>{tab.emoji}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Overview */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 lg:col-span-2 space-y-4 shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <span>📉</span> {t('last_6_months')}
              </h3>
              <div className="flex gap-3 text-xs font-semibold">
                <span className="flex items-center gap-1.5 text-green-600">
                  <span className="w-2.5 h-2.5 bg-green-500 rounded-sm" /> {t('chart_revenue')}
                </span>
                <span className="flex items-center gap-1.5 text-red-500">
                  <span className="w-2.5 h-2.5 bg-red-500 rounded-sm" /> {t('chart_expenses')}
                </span>
              </div>
            </div>
            
            {/* SVG Chart */}
            <div className="flex justify-center bg-gray-50 rounded-xl p-3 border border-gray-100 overflow-x-auto">
              <svg width={svgW} height={svgH} className="w-full max-w-2xl">
                {/* Y-axis gridlines */}
                {[0, 0.25, 0.5, 0.75, 1].map((p, idx) => {
                  const y = padY + graphH * (1 - p);
                  return (
                    <g key={idx}>
                      <line x1={padX} y1={y} x2={svgW - padX} y2={y} stroke="#e2e8f0" strokeDasharray="4 4" />
                      <text 
                        x={isAr ? svgW - padX + 24 : padX - 8} 
                        y={y + 4} 
                        textAnchor={isAr ? 'start' : 'end'} 
                        fontSize="9px" 
                        className="fill-gray-400 font-semibold"
                      >
                        {Math.round(maxChartVal * p).toLocaleString()}
                      </text>
                    </g>
                  );
                })}
                
                {/* Columns */}
                {chartData.map((d, i) => {
                  const xCenter = padX + i * step + step / 2;
                  const barW = 16;
                  const spacing = 2;
                  
                  const revH = (d.revenue / maxChartVal) * graphH;
                  const expH = (d.expenses / maxChartVal) * graphH;
                  
                  const revY = padY + graphH - revH;
                  const expY = padY + graphH - expH;
                  
                  const monthLabel = (() => {
                    const parts = d.month.split('-');
                    const monthsNames = isAr
                      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
                      : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
                    return `${monthsNames[parseInt(parts[1]) - 1]} ${parts[0]}`;
                  })();

                  return (
                    <g key={d.month}>
                      <rect
                        x={xCenter - barW - spacing}
                        y={revY}
                        width={barW}
                        height={revH || 1}
                        rx={3}
                        className="fill-green-500 hover:fill-green-600 transition duration-150 cursor-pointer"
                      >
                        <title>{`${t('chart_revenue')} : ${formattedValue(d.revenue)}`}</title>
                      </rect>
                      <rect
                        x={xCenter + spacing}
                        y={expY}
                        width={barW}
                        height={expH || 1}
                        rx={3}
                        className="fill-red-500 hover:fill-red-600 transition duration-150 cursor-pointer"
                      >
                        <title>{`${t('chart_expenses')} : ${formattedValue(d.expenses)}`}</title>
                      </rect>
                      <text
                        x={xCenter}
                        y={svgH - 10}
                        textAnchor="middle"
                        fontSize="11px"
                        className="fill-gray-600 font-bold"
                      >
                        {monthLabel}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Side card breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4 shadow-sm">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 border-b border-gray-100 pb-3">
              <span>📋</span> {t('details_charges')}
            </h3>
            <div className="space-y-4">
              {[
                { label: t('entretien_vidange'), val: totalVidanges, color: 'bg-amber-500', enabled: settings.financeIncludeVidanges !== false },
                { label: t('remboursement_credits'), val: totalCredits, color: 'bg-indigo-500', enabled: settings.financeIncludeCredits !== false },
                { label: t('charges_exploitation'), val: totalGeneralExpenses, color: 'bg-rose-500', enabled: settings.financeIncludeExpenses !== false },
                { label: isAr ? '👥 رواتب الموظفين' : '👥 Salaires personnel', val: totalStaffSalaries, color: 'bg-blue-500', enabled: settings.financeIncludeStaff !== false },
                { label: isAr ? '⏩ سلفات الموظفين' : '⏩ Avances personnel', val: totalStaffAdvances, color: 'bg-purple-500', enabled: settings.financeIncludeStaffAdvances !== false },
                { label: isAr ? '📤 شيكات صادرة' : '📤 Chèques émis', val: totalChequesPaid, color: 'bg-orange-500', enabled: settings.financeIncludeChequesPaid !== false },
                { label: isAr ? '🏦 ترايتات بنكية' : '🏦 Traites bancaires', val: totalBankInstallments, color: 'bg-cyan-600', enabled: settings.financeIncludeBankInstallments !== false }
              ].filter(item => item.enabled && item.val > 0).map((item, idx) => {
                const percentage = totalExpenses > 0 ? (item.val / totalExpenses) * 100 : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-gray-600 font-medium">{item.label}</span>
                      <span className="text-gray-900 font-bold">{`${formattedValue(item.val)} (${percentage.toFixed(0)}%)`}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3.5 text-xs text-blue-800 leading-relaxed font-medium mt-4">
              <strong className="block font-bold mb-1">{t('tip_title')}</strong>
              {t('tip_content')}
            </div>
          </div>
        </div>
      )}

      {/* Per Car Performance */}
      {activeTab === 'perCar' && (
        <div className="space-y-6">
          {(topPerformer || worstPerformer) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topPerformer && topPerformer.revenue > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-300 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">🏆</div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-green-700 uppercase mb-1">
                        {isAr ? '🥇 الأكثر ربحاً' : '🥇 Top Performer'}
                      </div>
                      <div className="text-lg font-bold text-green-900">{topPerformer.carName}</div>
                      <div className="text-xs text-green-700 font-mono mb-2">{topPerformer.plate}</div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-green-700">{isAr ? 'الدخل: ' : 'Revenu: '}</span>
                          <strong className="text-green-900">{topPerformer.revenue.toFixed(0)} {settings.currency || 'MAD'}</strong>
                        </div>
                        <div>
                          <span className="text-green-700">{isAr ? 'الربح: ' : 'Profit: '}</span>
                          <strong className="text-green-900">{topPerformer.netProfit.toFixed(0)} {settings.currency || 'MAD'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {worstPerformer && worstPerformer.carId !== topPerformer?.carId && worstPerformer.rentalsCount > 0 && (
                <div className="bg-gradient-to-br from-red-50 to-orange-100 border-2 border-red-300 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">{worstPerformer.netProfit < 0 ? '📉' : '⚠️'}</div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-red-700 uppercase mb-1">
                        {isAr ? '🔻 الأقل أداءاً' : '🔻 Moins rentable'}
                      </div>
                      <div className="text-lg font-bold text-red-900">{worstPerformer.carName}</div>
                      <div className="text-xs text-red-700 font-mono mb-2">{worstPerformer.plate}</div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-red-700">{isAr ? 'الدخل: ' : 'Revenu: '}</span>
                          <strong className="text-red-900">{worstPerformer.revenue.toFixed(0)} {settings.currency || 'MAD'}</strong>
                        </div>
                        <div>
                          <span className="text-red-700">{isAr ? 'الربح: ' : 'Profit: '}</span>
                          <strong className="text-red-900">{worstPerformer.netProfit.toFixed(0)} {settings.currency || 'MAD'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Car className="w-5 h-5 text-blue-600" />
                {isAr ? '🚗 الأداء المالي لكل سيارة' : '🚗 Performance financière par voiture'}
              </h2>
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-gray-600">
                  {isAr ? 'ترتيب حسب:' : 'Trier par:'}
                </label>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium bg-white"
                >
                  <option value="revenue">{isAr ? '💰 الدخل' : '💰 Revenu'}</option>
                  <option value="profit">{isAr ? '📈 الربح الصافي' : '📈 Profit net'}</option>
                  <option value="rentals">{isAr ? '📋 عدد الإيجارات' : '📋 Nb locations'}</option>
                  <option value="days">{isAr ? '📅 أيام الإيجار' : '📅 Jours loués'}</option>
                  <option value="expenses">{isAr ? '💸 المصاريف' : '💸 Dépenses'}</option>
                  <option value="margin">{isAr ? '📊 هامش الربح' : '📊 Marge %'}</option>
                  <option value="name">{isAr ? '🔤 الإسم' : '🔤 Nom'}</option>
                </select>
              </div>
            </div>

            {sortedCarStats.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-2">🚗</div>
                <p>{isAr ? 'لا توجد سيارات' : 'Aucune voiture'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-y border-gray-200">
                    <tr className="text-xs font-bold text-gray-600 uppercase">
                      <th className="px-6 py-3 text-left">{isAr ? 'السيارة' : 'Voiture'}</th>
                      <th className="px-6 py-3 text-center">{isAr ? 'الإيجارات' : 'Locations'}</th>
                      <th className="px-6 py-3 text-center">{isAr ? 'الأيام' : 'Jours'}</th>
                      <th className="px-6 py-3 text-right">{isAr ? '💰 الدخل' : '💰 Revenu'}</th>
                      <th className="px-6 py-3 text-right">{isAr ? '💸 المصاريف' : '💸 Dépenses'}</th>
                      <th className="px-6 py-3 text-right">{isAr ? '📈 الربح' : '📈 Profit'}</th>
                      <th className="px-6 py-3 text-center">{isAr ? 'الهامش' : 'Marge'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedCarStats.map((s, idx) => {
                      const rank = idx + 1;
                      const isProfit = s.netProfit > 0;
                      const isLoss = s.netProfit < 0;
                      
                      return (
                        <tr 
                          key={s.carId}
                          className={`hover:bg-gray-50/50 transition-colors ${s.status === 'deleted' ? 'opacity-50' : ''}`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                                rank === 2 ? 'bg-gray-200 text-gray-700' :
                                rank === 3 ? 'bg-orange-100 text-orange-800' :
                                'bg-blue-50 text-blue-700'
                              }`}>
                                {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`}
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">{s.carName}</div>
                                <div className="text-xs text-gray-500 font-mono">{s.plate}</div>
                                {s.lastRentalDate && (
                                  <div className="text-[10px] text-gray-400 mt-0.5">
                                    {isAr ? `آخر إيجار: ${s.lastRentalDate}` : `Dernière: ${s.lastRentalDate}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold">
                              {s.rentalsCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-gray-700 font-mono">
                            {s.totalDays}j
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="font-bold text-green-700 font-mono">
                              {s.revenue.toFixed(0)} {settings.currency || 'MAD'}
                            </div>
                            {s.avgRevenuePerDay > 0 && (
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {isAr ? `${s.avgRevenuePerDay.toFixed(0)}/يوم` : `${s.avgRevenuePerDay.toFixed(0)}/jour`}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="font-bold text-red-650 text-red-500 font-mono">
                              {s.totalExpenses.toFixed(0)} {settings.currency || 'MAD'}
                            </div>
                            <div className="text-[10px] text-gray-500 mt-0.5 space-x-1">
                              {s.vidangesCost > 0 && <span title="Vidanges">🔧{s.vidangesCost.toFixed(0)}</span>}
                              {' '}
                              {(s.creditPayments + s.bankInstallmentsPaid) > 0 && (
                                <span title="Crédit">🏦{(s.creditPayments + s.bankInstallmentsPaid).toFixed(0)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right font-bold font-mono">
                            <span className={isProfit ? 'text-emerald-700' : isLoss ? 'text-red-600' : 'text-gray-600'}>
                              {isProfit ? '+' : ''}{s.netProfit.toFixed(0)} {settings.currency || 'MAD'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {s.revenue > 0 ? (
                              <div className="inline-flex flex-col items-center">
                                <span className={`text-xs font-bold ${
                                  s.profitMargin >= 50 ? 'text-emerald-700' :
                                  s.profitMargin >= 20 ? 'text-green-600' :
                                  s.profitMargin >= 0 ? 'text-amber-600' :
                                  'text-red-650 text-red-600'
                                }`}>
                                  {s.profitMargin.toFixed(0)}%
                                </span>
                                <div className="w-12 h-1 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                  <div 
                                    className={`h-full ${
                                      s.profitMargin >= 50 ? 'bg-emerald-500' :
                                      s.profitMargin >= 20 ? 'bg-green-500' :
                                      s.profitMargin >= 0 ? 'bg-amber-500' :
                                      'bg-red-500'
                                    }`}
                                    style={{ width: `${Math.min(100, Math.max(0, Math.abs(s.profitMargin)))}%` }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                    <tr>
                      <td className="px-6 py-3 text-sm">{isAr ? '📊 المجموع' : '📊 TOTAL'}</td>
                      <td className="px-6 py-3 text-center">
                        {sortedCarStats.reduce((s, c) => s + c.rentalsCount, 0)}
                      </td>
                      <td className="px-6 py-3 text-center font-mono">
                        {sortedCarStats.reduce((s, c) => s + c.totalDays, 0)}j
                      </td>
                      <td className="px-6 py-3 text-right text-green-700 font-mono">
                        {sortedCarStats.reduce((s, c) => s + c.revenue, 0).toFixed(0)} {settings.currency || 'MAD'}
                      </td>
                      <td className="px-6 py-3 text-right text-red-650 text-red-500 font-mono">
                        {sortedCarStats.reduce((s, c) => s + c.totalExpenses, 0).toFixed(0)} {settings.currency || 'MAD'}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {(() => {
                          const totalProfit = sortedCarStats.reduce((s, c) => s + c.netProfit, 0);
                          return (
                            <span className={`font-mono ${totalProfit > 0 ? 'text-emerald-700' : totalProfit < 0 ? 'text-red-650 text-red-650 text-red-600' : 'text-gray-650 text-gray-600'}`}>
                              {totalProfit > 0 ? '+' : ''}{totalProfit.toFixed(0)} {settings.currency || 'MAD'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          
          {/* Insights Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2 text-sm">
              💡 {isAr ? 'نصائح:' : 'Conseils:'}
            </h3>
            <ul className="text-xs text-blue-800 space-y-1 list-disc pl-5">
              <li>
                {isAr 
                  ? 'السيارات بهامش ربح > 50% هي الأكثر ربحية. ركّز عليها.' 
                  : 'Les voitures avec marge > 50% sont les plus rentables. Concentrez-vous dessus.'}
              </li>
              <li>
                {isAr 
                  ? 'إذا كانت السيارة في خسارة، راجع: التكاليف العالية (vidanges) أو قسط بنكي.' 
                  : 'Si une voiture est en perte, vérifiez: vidanges coûteuses ou traites bancaires.'}
              </li>
              <li>
                {isAr 
                  ? 'الدخل اليومي يساعدك على تحديد السعر الأمثل لكل سيارة.' 
                  : 'Le revenu/jour aide à fixer le prix optimal pour chaque voiture.'}
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Monthly Followup */}
      {activeTab === 'monthly' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_month')}</th>
                  <th className={`px-6 py-4 text-green-600 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_revenue_ca')}</th>
                  <th className={`px-6 py-4 text-red-500 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_expenses')}</th>
                  <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_net_profit')}</th>
                  <th className="px-6 py-4 text-center">{t('col_status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {monthlyStats.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400 font-medium italic">
                      {t('no_data_monthly')}
                    </td>
                  </tr>
                ) : (
                  monthlyStats.map(m => {
                    const monthProfit = m.revenue - m.expenses;
                    const parts = m.month.split('-');
                    const monthsNames = isAr
                      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
                      : ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                    const prettyMonth = `${monthsNames[parseInt(parts[1]) - 1]} ${parts[0]}`;

                    return (
                      <tr key={m.month} className="hover:bg-gray-50/50 transition-colors">
                        <td className={`px-6 py-4 font-bold text-gray-900 ${isAr ? 'text-right' : 'text-left'}`}>{prettyMonth}</td>
                        <td className={`px-6 py-4 text-green-600 font-semibold ${isAr ? 'text-right' : 'text-left'}`}>+{formattedValue(m.revenue)}</td>
                        <td className={`px-6 py-4 text-red-500 font-semibold ${isAr ? 'text-right' : 'text-left'}`}>-{formattedValue(m.expenses)}</td>
                        <td className={`px-6 py-4 font-bold ${isAr ? 'text-right' : 'text-left'} ${monthProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {monthProfit >= 0 ? `+${formattedValue(monthProfit)}` : formattedValue(monthProfit)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${
                            monthProfit >= 0 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {monthProfit >= 0 ? t('status_profit') : t('status_loss')}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily Followup */}
      {activeTab === 'daily' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_date')}</th>
                  <th className={`px-6 py-4 text-green-600 ${isAr ? 'text-right' : 'text-left'}`}>{t('chart_revenue')}</th>
                  <th className={`px-6 py-4 text-red-500 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_expenses')}</th>
                  <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_net_profit')}</th>
                  <th className="px-6 py-4 text-center">{t('col_status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {dailyStats.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400 font-medium italic">
                      {t('no_data_daily')}
                    </td>
                  </tr>
                ) : (
                  dailyStats.map(d => {
                    const dayProfit = d.revenue - d.expenses;
                    const prettyDate = new Date(d.date).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR', { 
                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                    });

                    return (
                      <tr key={d.date} className="hover:bg-gray-50/50 transition-colors">
                        <td className={`px-6 py-4 font-bold text-gray-900 ${isAr ? 'text-right' : 'text-left'}`}>{prettyDate}</td>
                        <td className={`px-6 py-4 text-green-600 font-semibold ${isAr ? 'text-right' : 'text-left'}`}>
                          {d.revenue > 0 ? `+${formattedValue(d.revenue)}` : '—'}
                        </td>
                        <td className={`px-6 py-4 text-red-500 font-semibold ${isAr ? 'text-right' : 'text-left'}`}>
                          {d.expenses > 0 ? `-${formattedValue(d.expenses)}` : '—'}
                        </td>
                        <td className={`px-6 py-4 font-bold ${isAr ? 'text-right' : 'text-left'} ${dayProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {dayProfit > 0 ? `+${formattedValue(dayProfit)}` : dayProfit < 0 ? formattedValue(dayProfit) : '—'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {dayProfit === 0 ? (
                            <span className="text-xs text-gray-400 font-semibold">{t('status_neutral')}</span>
                          ) : (
                            <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${
                              dayProfit > 0 
                                ? 'bg-green-50 text-green-700 border-green-200' 
                                : 'bg-red-50 text-red-700 border-red-200'
                            }`}>
                              {dayProfit > 0 ? t('status_profit') : t('status_loss')}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* General Expenses Management */}
      {activeTab === 'generalExpenses' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <span>💸</span> {t('add_expense')}
              </h3>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-100 font-bold transition-all shadow-sm"
              >
                {showAddForm ? t('btn_close_form') : t('btn_add_expense')}
              </button>
            </div>

            {showAddForm && (
              <form onSubmit={handleAddExpense} className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('label_desc_title')}
                  </label>
                  <input
                    type="text"
                    value={expTitle}
                    onChange={e => setExpTitle(e.target.value)}
                    placeholder={t('placeholder_desc')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {`${t('label_amount')} (${settings.currency || 'MAD'}) *`}
                  </label>
                  <input
                    type="number"
                    value={expAmount}
                    onChange={e => setExpAmount(e.target.value)}
                    placeholder={t('placeholder_amount')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('label_category')}
                  </label>
                  <select
                    value={expCategory}
                    onChange={e => setExpCategory(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('label_date')}
                  </label>
                  <div className="flex gap-2 flex-1">
                    <input
                      type="date"
                      value={expDate}
                      onChange={e => setExpDate(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 bg-white cursor-pointer"
                    />
                    <button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4.5 rounded-lg text-sm font-bold flex items-center justify-center transition shadow-sm"
                    >
                      {t('register_btn')}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* List of general expenses */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('col_date')}</th>
                    <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{isAr ? 'المصروف' : 'Dépense'}</th>
                    <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{t('label_category')}</th>
                    <th className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>{isAr ? 'المبلغ' : 'Montant'}</th>
                    <th className={`px-6 py-4 ${isAr ? 'text-left' : 'text-right'}`}>{t('col_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {expenses.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-400 font-medium italic">
                        {t('no_expenses')}
                      </td>
                    </tr>
                  ) : (
                    [...expenses].sort((a,b) => b.date.localeCompare(a.date)).map(exp => {
                      const prettyDate = new Date(exp.date).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR', { 
                        day: 'numeric', month: 'short', year: 'numeric' 
                      });
                      const categoryColors = {
                        Loyer: 'bg-purple-50 text-purple-700 border-purple-200',
                        Assurance: 'bg-blue-50 text-blue-700 border-blue-200',
                        Lavage: 'bg-cyan-50 text-cyan-700 border-cyan-200',
                        Carburant: 'bg-amber-50 text-amber-700 border-amber-200',
                        Salaires: 'bg-indigo-50 text-indigo-700 border-indigo-200',
                        Autre: 'bg-gray-50 text-gray-700 border-gray-200'
                      };

                      return (
                        <tr key={exp.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className={`px-6 py-4 font-semibold text-gray-500 ${isAr ? 'text-right' : 'text-left'}`}>{prettyDate}</td>
                          <td className={`px-6 py-4 font-bold text-gray-900 ${isAr ? 'text-right' : 'text-left'}`}>{exp.title}</td>
                          <td className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full border ${categoryColors[exp.category] || categoryColors.Autre}`}>
                              {t('cat_' + (exp.category || 'autre').toLowerCase())}
                            </span>
                          </td>
                          <td className={`px-6 py-4 font-bold text-red-500 ${isAr ? 'text-right' : 'text-left'}`}>
                            -{formattedValue(exp.amount)}
                          </td>
                          <td className={`px-6 py-4 ${isAr ? 'text-left' : 'text-right'}`}>
                            <button
                              onClick={() => handleDeleteExpense(exp.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition inline-flex items-center justify-center cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
